import { useCallback, useEffect, useState } from "react";
import { Spinner, Text, VStack } from "@chakra-ui/react";
import ClientService from "@/services/ClientService";
import { showToast } from "@/components/ui/toaster";
import { SectionCard } from "@/components/ui/detail/primitives";
import CompanyMembersTable from "@/pages/clients/components/CompanyMembersTable";
import MemberFormDialog from "@/pages/clients/components/MemberFormDialog";
import ConfirmDialog from "@/pages/clients/components/ConfirmDialog";

/**
 * "Użytkownicy" section of the client-detail page: the Owner row plus every user invited under
 * the company. Reuses the same table / dialogs as the Clients list page. The Owner row is shown
 * read-only here — its active state is managed by the profile card's "Konto aktywne" switch.
 */
const ClientMembersSection = ({ client, onMembersChanged }) => {
    const [members, setMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [memberDialog, setMemberDialog] = useState(null); // { mode, member }
    const [confirm, setConfirm] = useState(null); // { member, action: 'deactivate' | 'resetTwoFactor' }
    const [confirmBusy, setConfirmBusy] = useState(false);

    const load = useCallback(() => {
        setIsLoading(true);
        ClientService.getMembers(client.id)
            .then((data) => {
                // The shared axios wrapper resolves (not rejects) on HTTP errors, returning the error
                // object — a successful response is always the CompanyUserDto array.
                if (Array.isArray(data)) {
                    setMembers(data);
                } else {
                    setMembers([]);
                    showToast("Błąd", "Nie udało się pobrać użytkowników", "error");
                }
            })
            .finally(() => setIsLoading(false));
    }, [client.id]);

    useEffect(() => { load(); }, [load]);

    const company = {
        id: client.id,
        companyName: client.companyName,
        displayName: client.companyName || `${client.firstName ?? ""} ${client.lastName ?? ""}`.trim(),
        owner: { isActive: client.isActive },
        users: members,
    };

    const runMutation = async (fn, msg) => {
        try {
            await fn();
            if (msg) showToast(msg, null, "success");
            load();
            onMembersChanged?.();
            return true;
        } catch (err) {
            showToast("Błąd", err?.response?.data ?? "Akcja nie powiodła się", "error");
            return false;
        }
    };

    const handleToggleMember = (member) => {
        if (member.role === "Owner") {
            showToast("Info", "Status właściciela zmieniasz przełącznikiem „Konto aktywne” u góry.", "info");
            return;
        }
        if (member.isActive) setConfirm({ member, action: "deactivate" });
        else runMutation(() => ClientService.activateMember(member.id), "Użytkownik aktywowany");
    };

    const handleEditMember = (member) => {
        if (member.role === "Owner") {
            showToast("Info", "Dane właściciela edytujesz w sekcji „Dane właściciela”.", "info");
            return;
        }
        setMemberDialog({ mode: "edit", member });
    };

    const runConfirm = async () => {
        if (!confirm) return;
        setConfirmBusy(true);
        const ok = confirm.action === "resetTwoFactor"
            ? await runMutation(() => ClientService.resetMemberTwoFactor(confirm.member.id), "2FA zresetowane")
            : await runMutation(() => ClientService.deactivateMember(confirm.member.id), "Użytkownik dezaktywowany");
        setConfirmBusy(false);
        if (ok) setConfirm(null);
    };

    return (
        <SectionCard title="Użytkownicy" contentProps={{ mx: -5, mb: -5 }}>
            {isLoading ? (
                <VStack py={8} align="center"><Spinner color="white" size="md" /></VStack>
            ) : (
                <CompanyMembersTable
                    company={company}
                    onEditMember={handleEditMember}
                    onToggleMember={handleToggleMember}
                    onResendInvitation={(m) => runMutation(() => ClientService.resendInvitation(m.id), "Zaproszenie wysłane")}
                    onResetTwoFactor={(m) => setConfirm({ member: m, action: "resetTwoFactor" })}
                    onAddMember={() => setMemberDialog({ mode: "add" })}
                />
            )}

            <MemberFormDialog
                open={!!memberDialog}
                mode={memberDialog?.mode}
                company={company}
                member={memberDialog?.member}
                onClose={() => setMemberDialog(null)}
                onSaved={() => { load(); onMembersChanged?.(); }}
            />
            <ConfirmDialog
                open={!!confirm}
                title={confirm?.action === "resetTwoFactor" ? "Zresetować 2FA?" : "Dezaktywować użytkownika?"}
                body={!confirm ? "" : confirm.action === "resetTwoFactor"
                    ? `Wyłączyć uwierzytelnianie dwuskładnikowe dla ${confirm.member.firstName} ${confirm.member.lastName} (${confirm.member.email})? Do czasu ponownej konfiguracji będzie logować się samym hasłem. Zrób to tylko po potwierdzeniu tożsamości tej osoby.`
                    : `Dezaktywować ${confirm.member.firstName} ${confirm.member.lastName} (${confirm.member.email})? Pozostałe konta firmy nie są dotknięte.`}
                confirmLabel={confirm?.action === "resetTwoFactor" ? "Resetuj 2FA" : "Dezaktywuj"}
                isLoading={confirmBusy}
                onCancel={() => !confirmBusy && setConfirm(null)}
                onConfirm={runConfirm}
            />
        </SectionCard>
    );
};

export default ClientMembersSection;
